import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { Place, RestFilters } from "@instead/shared";
import { api } from "../lib/api.ts";
import { locateOnce } from "../lib/context.ts";
import { loadFilters, saveFilters, TIME_STEPS } from "../lib/filters.ts";
import { moodLabel, useT } from "../lib/i18n.ts";
import { ChipGroup } from "../components/Ui.tsx";
import { Cover } from "../components/Cover.tsx";
import { Ic } from "../components/Icons.tsx";

/**
 * Home (rounds 3–4): one question, an inline time wheel (5 min default,
 * Any on top), OR multi-select chips, the needs on hand, where you are —
 * everything remembered on the device — then Shuffle or Pick my own.
 * Option rows show only when their feature is on; below sit the saved
 * selections (and the current deal when there is one).
 */
const SEEN_KEY = "instead.sources.seen";

export function Rest() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [filters, setFilters] = useState<RestFilters>(loadFilters);
  const [locating, setLocating] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const { data: shelf } = useQuery({ queryKey: ["items", "count"], queryFn: () => api.items.list({ limit: "1" }) });
  const { data: moodsData } = useQuery({ queryKey: ["moods"], queryFn: api.moods.list });
  const contextOn = settings?.features.context ?? false;
  const needsOn = settings?.features.needs ?? false;
  const { data: placesData } = useQuery({ queryKey: ["places"], queryFn: api.places.list, enabled: contextOn });
  const { data: needsData } = useQuery({ queryKey: ["needs"], queryFn: api.needs.list, enabled: needsOn });
  const { data: selData } = useQuery({ queryKey: ["selections"], queryFn: api.selections.list });
  const { data: dealData } = useQuery({ queryKey: ["deal"], queryFn: api.deal.get });
  const shelfEmpty = (shelf?.items.length ?? 0) === 0;
  // what sources added since this device last looked (round 5)
  const [seenAt, setSeenAt] = useState<string>(() => { try { return localStorage.getItem(SEEN_KEY) ?? new Date().toISOString(); } catch { return new Date().toISOString(); } });
  const { data: fresh } = useQuery({ queryKey: ["items", "fresh", seenAt], queryFn: () => api.items.list({ fromSources: "1", createdAfter: seenAt, limit: "200" }) });
  const freshCount = fresh?.items.length ?? 0;
  const markSeen = () => { const now = new Date().toISOString(); try { localStorage.setItem(SEEN_KEY, now); } catch { /* fine */ } setSeenAt(now); };
  useEffect(() => { try { if (!localStorage.getItem(SEEN_KEY)) localStorage.setItem(SEEN_KEY, seenAt); } catch { /* fine */ } }, [seenAt]);

  const update = (patch: Partial<RestFilters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    saveFilters(next);
  };

  // needs are a hard filter only while the feature is on
  useEffect(() => {
    if (!settings) return;
    if (!needsOn && filters.haveNeedIds) update({ haveNeedIds: undefined });
    if (needsOn && !filters.haveNeedIds) update({ haveNeedIds: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsOn, settings]);

  function pickWhere(kind: RestFilters["where"]["kind"], place?: Place) {
    if (kind === "place" && place) {
      // a place autofills what's on hand from what it offers (round 4)
      update({ where: { kind: "place", placeId: place.id }, ...(needsOn ? { haveNeedIds: place.needIds } : {}) });
    } else {
      update({ where: { kind } });
    }
  }

  async function locate() {
    setLocating(null);
    try {
      const match = await locateOnce(placesData?.places ?? []);
      if (match) pickWhere("place", match);
      else setLocating(t("rest.noPlaceMatch"));
    } catch {
      setLocating(t("places.geoFailed"));
    }
  }

  async function shuffle() {
    setBusy(true);
    try {
      const r = await api.picks.shuffle({ count: settings?.rest.defaultCards ?? 3, filters, includeDone: settings?.rest.includeDone ?? false });
      // the Deal screen reads the cached deal first — hand it the fresh one
      qc.setQueryData(["deal"], { deal: { pickId: r.pickId, itemIds: r.itemIds, filters, relaxed: r.relaxed, createdAt: new Date().toISOString() } });
      await nav({ to: "/deal" });
    } finally {
      setBusy(false);
    }
  }

  const moods = moodsData?.moods ?? [];
  const places = placesData?.places ?? [];
  const needs = needsData?.needs ?? [];
  const deal = dealData?.deal ?? null;
  const selections = selData?.selections ?? [];

  return (
    <div className="rest-col rest-hero" style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)", paddingTop: "var(--sp-4)" }}>
      <h1 className="display">{t("rest.question")}</h1>
      {freshCount > 0 && (
        <div className="banner" role="status" style={{ width: "100%" }}>
          <Link to="/shelf" style={{ flex: 1, color: "inherit", textDecoration: "none" }} onClick={markSeen}>{freshCount === 1 ? t("rest.newSinceOne") : t("rest.newSince", { n: freshCount })}</Link>
          <button className="icon-btn" style={{ minWidth: 32, minHeight: 32 }} aria-label={t("done")} onClick={markSeen}>{Ic.x({ size: 14 })}</button>
        </div>
      )}

      <div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>{t("rest.time")}</div>
        <TimeWheel value={filters.timeMin} onChange={(v) => update({ timeMin: v })} />
      </div>

      {moods.length > 0 && (
        <div>
          <div className="eyebrow" style={{ justifyContent: "center" }}>{t("rest.feel")}</div>
          <ChipGroup center multi label={t("rest.feel")} selected={filters.moods}
            options={moods.map((m) => ({ value: m.key, label: moodLabel(m) }))}
            onChange={(moods) => update({ moods })} />
        </div>
      )}

      <div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>{t("rest.want")}</div>
        <ChipGroup center multi label={t("rest.want")} selected={filters.orientations}
          options={(["create", "consume", "learn", "fun", "be"] as const).map((o) => ({ value: o, label: t(`want.${o}`) }))}
          onChange={(orientations) => update({ orientations: orientations as RestFilters["orientations"] })} />
      </div>

      {needsOn && needs.length > 0 && (
        <div>
          <div className="eyebrow" style={{ justifyContent: "center" }}>{t("rest.have")}</div>
          <ChipGroup center multi label={t("rest.have")} selected={filters.haveNeedIds ?? []}
            options={needs.map((n) => ({ value: n.id, label: n.name }))}
            onChange={(haveNeedIds) => update({ haveNeedIds })} />
        </div>
      )}

      {contextOn && (
        <div>
          <div className="eyebrow" style={{ justifyContent: "center" }}>{t("rest.where")}</div>
          <div role="group" aria-label={t("rest.where")} className="chip-row" style={{ justifyContent: "center" }}>
            {(["anywhere", "indoor", "outdoor"] as const).map((k) => (
              <button key={k} type="button" className="chip" aria-pressed={filters.where.kind === k} onClick={() => pickWhere(k)}>
                {t(k === "anywhere" ? "rest.anywhere" : k === "indoor" ? "rest.indoors" : "rest.outdoors")}
              </button>
            ))}
            {places.map((p) => (
              <button key={p.id} type="button" className="chip" aria-pressed={filters.where.kind === "place" && filters.where.placeId === p.id} onClick={() => pickWhere("place", p)}>
                {p.name}
              </button>
            ))}
            {places.some((p) => p.lat != null) && (
              <button type="button" className="chip" onClick={locate}>{Ic.locate({ size: 12 })} {t("rest.locate")}</button>
            )}
          </div>
          {locating && <p role="status" className="micro" style={{ marginTop: "var(--sp-1)" }}>{locating}</p>}
        </div>
      )}

      {shelfEmpty ? (
        <div className="empty">
          <p>{t("rest.empty")}</p>
          <Link className="btn btn-primary" to="/add">{t("shelf.add")}</Link>
        </div>
      ) : (
        <>
          <button className="btn btn-primary btn-lg" disabled={busy} onClick={shuffle} style={{ width: 260, margin: "0 auto" }}>
            {t("rest.shuffle")}
          </button>
          <Link className="btn btn-ghost" to="/pick" style={{ margin: "0 auto" }}>{t("rest.pickOwn")}</Link>
        </>
      )}

      {(deal || selections.length > 0) && (
        <div style={{ width: "100%", textAlign: "left", marginTop: "var(--sp-2)" }}>
          <div className="eyebrow">{t("rest.selections")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
            {deal && deal.itemIds.length > 0 && (
              <Link className="sel-row" to="/deal">
                <SelThumbs ids={deal.itemIds} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 600 }}>{t("deal.title")}</span>
                  <span className="meta">{deal.itemIds.length === 1 ? t("rest.item") : t("rest.items", { n: deal.itemIds.length })}</span>
                </span>
                <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>{Ic.chevron({ size: 16 })}</span>
              </Link>
            )}
            {selections.map((s) => (
              <Link key={s.id} className="sel-row" to="/set/$id" params={{ id: s.id }}>
                <SelThumbs ids={s.itemIds} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 600 }}>{s.origin === "ai" && s.title === "Picked with your AI" ? t("set.aiTitle") : s.title}</span>
                  <span className="meta">{s.itemIds.length === 1 ? t("rest.item") : t("rest.items", { n: s.itemIds.length })}</span>
                </span>
                <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>{Ic.chevron({ size: 16 })}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SelThumbs({ ids }: { ids: string[] }) {
  const { data } = useQuery({ queryKey: ["items", "byIds", ids.slice(0, 3).join(",")], queryFn: () => api.items.byIds(ids.slice(0, 3)) });
  return (
    <span className="sel-thumbs" aria-hidden="true">
      {(data ?? []).map((i) => <Cover key={i.id} item={i} size="s" />)}
    </span>
  );
}

/** The inline time wheel: Any on top, then the minute steps; snaps to rows. */
function TimeWheel({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const rows: (number | null)[] = [null, ...TIME_STEPS];
  const index = rows.findIndex((r) => r === value);
  const settling = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const i = Math.max(0, index);
    el.scrollTo({ top: i * 30, behavior: "instant" as ScrollBehavior });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    if (settling.current) window.clearTimeout(settling.current);
    settling.current = window.setTimeout(() => {
      const i = Math.round(el.scrollTop / 30);
      const v = rows[Math.min(rows.length - 1, Math.max(0, i))] ?? null;
      if (v !== value) onChange(v);
    }, 90);
  };

  const label = (v: number | null) => (v == null ? t("rest.any") : `${v} ${t("rest.min")}`);
  return (
    <div className="wheel" ref={ref} onScroll={onScroll} role="listbox" aria-label={t("rest.time")} tabIndex={0}
      onKeyDown={(e) => {
        const i = Math.max(0, index);
        if (e.key === "ArrowDown" && i < rows.length - 1) { e.preventDefault(); onChange(rows[i + 1] ?? null); ref.current?.scrollTo({ top: (i + 1) * 30 }); }
        if (e.key === "ArrowUp" && i > 0) { e.preventDefault(); onChange(rows[i - 1] ?? null); ref.current?.scrollTo({ top: (i - 1) * 30 }); }
      }}>
      <div className="wheel-pad" aria-hidden="true" />
      {rows.map((r, i) => (
        <button type="button" key={String(r)} className="wheel-row" role="option" aria-selected={r === value}
          onClick={() => { onChange(r); ref.current?.scrollTo({ top: i * 30, behavior: "smooth" }); }}>
          {label(r)}
        </button>
      ))}
      <div className="wheel-pad" aria-hidden="true" />
    </div>
  );
}
